import type { Office } from "./types"

export const BATH_SPECIALISMS = [
  "Residential Sales",
  "Lettings",
  "Property Management",
  "New Homes",
  "Commercial",
  "Land & Development",
  "Auctions",
  "Relocation",
] as const

export const BATH_AREAS = [
  "Bath City Centre",
  "Bathwick",
  "Widcombe",
  "Lansdown",
  "Bear Flat",
  "Combe Down",
  "Oldfield Park",
  "Weston",
  "Larkhall",
  "Bathampton",
  "Batheaston",
  "Bathford",
  "Odd Down",
  "Twerton",
  "Newbridge",
  "Lower Weston",
  "Camden",
  "Walcot",
] as const

export const BATH_OFFICES: Office[] = [
  {
    slug: "savills-bath",
    name: "Savills",
    branch: "Bath",
    address: "6 Edgar Buildings, George Street",
    postcode: "BA1 2EE",
    phone: "01225 474500",
    website: "https://www.savills.co.uk/estate-agents/south-west-england/savills-bath.aspx",
    email: "bath@savills.com",
    description:
      "Savills Bath occupies a prime position on George Street in one of the city's finest Georgian buildings. Their team of experienced agents covers sales and lettings across Bath, the surrounding countryside, and into north-east Somerset. With deep expertise in period and listed properties, they are a natural choice for premium homes in the World Heritage City.",
    established: 1855,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Land & Development"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Bathampton", "Bathford"],
    propertyCount: 75,
    avgPrice: "£825,000",
    rating: 4.6,
    reviewCount: 118,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Savills has been advising property owners since 1855, and their Bath office continues that tradition from a commanding position in Edgar Buildings on George Street. The team here combines Savills' global reach with a genuine feel for Bath's unique market, where listed Georgian townhouses sit alongside village properties in the surrounding countryside.\n\nTheir agents bring particular strength in period and heritage properties, advising on everything from Grade I listed crescents to converted coach houses in the nearby villages of Bathampton and Bathford. Sellers benefit from Savills' international buyer network, which regularly connects Bath properties with relocating professionals and overseas purchasers.\n\nWith 118 client reviews and a 4.6 rating, the Bath team has earned a reputation for thorough market appraisals, considered pricing, and polished marketing that does justice to the city's most distinctive homes.",
    whyChoose: [
      "Over 165 years of estate agency heritage",
      "Rated 4.6/5 from 118 client reviews",
      "International buyer network for premium Bath properties",
      "Specialists in period, listed, and new-build homes",
    ],
    faq: [
      {
        question: "What areas does Savills Bath cover?",
        answer:
          "Savills Bath covers Bath City Centre, Lansdown, Bathwick, Widcombe, Bathampton, and Bathford. They also handle properties in the wider north-east Somerset countryside surrounding the city.",
      },
      {
        question: "What services does Savills offer in Bath?",
        answer:
          "Savills Bath provides residential sales, lettings, new homes, and land and development services. Their team advises on everything from city-centre apartments to country houses and development sites.",
      },
      {
        question: "What are Savills Bath's opening hours?",
        answer:
          "Savills Bath is open Monday to Friday from 08:30 to 18:00 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "knight-frank-bath",
    name: "Knight Frank",
    branch: "Bath",
    address: "2–3 Wood Street",
    postcode: "BA1 2JQ",
    phone: "01225 325999",
    website: "https://www.knightfrank.co.uk/offices/bath-estate-agents",
    email: "bath@knightfrank.com",
    description:
      "Knight Frank's Bath office is situated in the heart of the city on Wood Street, close to Queen Square. As one of the world's leading property consultancies, their Bath team brings international reach alongside intimate local knowledge of the Georgian city and the Cotswold and Mendip fringes. They handle premium residential sales and country property across the region.",
    established: 1896,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Land & Development", "Relocation"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Bathampton", "Bathford"],
    propertyCount: 55,
    avgPrice: "£975,000",
    rating: 4.7,
    reviewCount: 86,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Knight Frank has operated from their Wood Street office since establishing a presence in Bath, bringing one of the world's most recognised property brands to the World Heritage City. Their position close to Queen Square places them at the heart of Bath's premium market, where Georgian architecture commands some of the highest prices in the south-west.\n\nThe Bath team specialises in advising clients who are buying or selling at the upper end of the market. With an average property price of £975,000, they handle some of the city's most significant residential transactions. Their relocation service is particularly valued by clients moving from London and internationally, offering a seamless transition into Bath life.\n\nBacked by Knight Frank's research division and global office network, the Bath team provides clients with market intelligence that few local agents can match. Their 4.7 rating from 86 reviews reflects a consistently high standard of service.",
    whyChoose: [
      "Rated 4.7/5 from 86 client reviews",
      "Global property network with deep Bath expertise",
      "Dedicated relocation service for London and international movers",
      "Specialists in premium sales, lettings, and land development",
    ],
    faq: [
      {
        question: "What areas does Knight Frank Bath cover?",
        answer:
          "Knight Frank Bath covers Bath City Centre, Lansdown, Bathwick, Widcombe, Bathampton, and Bathford. They also advise on country property across the Cotswold and Mendip fringes.",
      },
      {
        question: "What services does Knight Frank offer in Bath?",
        answer:
          "Knight Frank Bath offers residential sales, lettings, new homes, land and development, and relocation services. They also provide property valuations and market appraisals for premium homes.",
      },
      {
        question: "What are Knight Frank Bath's opening hours?",
        answer:
          "Knight Frank Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "carter-jonas-bath",
    name: "Carter Jonas",
    branch: "Bath",
    address: "5 Quiet Street",
    postcode: "BA1 2JS",
    phone: "01225 747240",
    website: "https://www.carterjonas.co.uk/offices/bath",
    email: "bath@carterjonas.co.uk",
    description:
      "Carter Jonas Bath is located on Quiet Street in the heart of the city, providing residential sales and lettings services across Bath and north-east Somerset. Part of a national practice with a strong country house heritage, their Bath team excels at advising on period homes, new developments, and rural property in the surrounding countryside.",
    established: 1855,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Land & Development"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Bathampton", "Bathford", "Batheaston"],
    propertyCount: 60,
    avgPrice: "£720,000",
    rating: 4.5,
    reviewCount: 94,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–12:30" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Carter Jonas was founded in 1855 and their Bath office on Quiet Street carries forward a heritage deeply rooted in country house and rural property. This pedigree makes them especially well suited to Bath, where the Georgian city blends into a patchwork of villages and countryside that stretches towards Batheaston, Bathampton, and Bathford.\n\nThe team advises on a wide spectrum of property types, from period townhouses in Lansdown and Bathwick to new developments and smallholdings on the rural fringe. Their land and development expertise also makes them a trusted adviser to landowners and developers looking at sites around Bath.\n\nWith 94 client reviews and a 4.5 rating, Carter Jonas Bath has built a loyal following among clients who value expert guidance without pretension. Their national network adds weight, while the local team keeps things personal.",
    whyChoose: [
      "Founded in 1855 with strong country house heritage",
      "Rated 4.5/5 from 94 client reviews",
      "Covers seven key Bath areas including rural villages",
      "Expert land and development advisory for the Bath region",
    ],
    faq: [
      {
        question: "What areas does Carter Jonas Bath cover?",
        answer:
          "Carter Jonas Bath covers Bath City Centre, Lansdown, Bathwick, Widcombe, Bathampton, Bathford, and Batheaston. Their rural property expertise extends into the wider north-east Somerset countryside.",
      },
      {
        question: "What services does Carter Jonas offer in Bath?",
        answer:
          "Carter Jonas Bath provides residential sales, lettings, new homes, and land and development services. They advise on everything from Georgian townhouses to rural property and development sites.",
      },
      {
        question: "What are Carter Jonas Bath's opening hours?",
        answer:
          "Carter Jonas Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 12:30. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "cobb-farr-bath",
    name: "Cobb Farr",
    branch: "Bath",
    address: "1 Bladud Buildings, The Paragon",
    postcode: "BA1 5LS",
    phone: "01225 333332",
    website: "https://www.cobbfarr.com",
    email: "info@cobbfarr.com",
    description:
      "Cobb Farr is an independent estate agent with offices in Bath and Bradford on Avon. Their Bath office on The Paragon is one of the city's most respected agencies, with decades of experience in the premium market. They specialise in period and character properties, country houses, and new homes across Bath and the surrounding villages.",
    established: 1999,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Land & Development"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Camden", "Walcot"],
    propertyCount: 50,
    avgPrice: "£685,000",
    rating: 4.8,
    reviewCount: 142,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Cobb Farr has established itself as one of Bath's most trusted independent agencies since opening in 1999. Their office on The Paragon, one of Bath's most beautiful Georgian terraces, sets the tone for an agency that understands period architecture and the discerning buyers it attracts.\n\nThe team specialises in character properties across Bath's most sought-after neighbourhoods: Lansdown, Bathwick, Camden, and Walcot, along with the city centre itself. They also have a strong track record with new-build homes and development plots. With a second office in Bradford on Avon, their reach extends into the surrounding countryside.\n\nA 4.8 rating from 142 reviews places Cobb Farr among the highest-rated agents in the city. Clients consistently praise their honest valuations, proactive communication, and the kind of local insight that only comes from years of independent, owner-led operation.",
    whyChoose: [
      "Rated 4.8/5 from 142 client reviews, among Bath's highest",
      "Independent agency with over 25 years in the Bath market",
      "Specialists in period and character properties",
      "Two offices covering Bath and Bradford on Avon",
    ],
    faq: [
      {
        question: "What areas does Cobb Farr Bath cover?",
        answer:
          "Cobb Farr Bath covers Bath City Centre, Lansdown, Bathwick, Widcombe, Camden, and Walcot. They also serve the wider area through their second office in Bradford on Avon.",
      },
      {
        question: "What services does Cobb Farr offer in Bath?",
        answer:
          "Cobb Farr offers residential sales, lettings, new homes, and land and development services. They specialise in period properties, character homes, and country houses across Bath.",
      },
      {
        question: "What are Cobb Farr Bath's opening hours?",
        answer:
          "Cobb Farr Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "hamptons-bath",
    name: "Hamptons",
    branch: "Bath",
    address: "11 Quiet Street",
    postcode: "BA1 2JS",
    phone: "01225 463535",
    website: "https://www.hamptons.co.uk/estate-agents/bath",
    email: "bath@hamptons.co.uk",
    description:
      "Hamptons Bath occupies a prominent position on Quiet Street, a short walk from the Royal Crescent. Part of the Connells Group, they combine a wide-reaching national buyer network with expert local knowledge. Their team specialises in premium and period property across the Georgian city, with particular strength in the Lansdown and Bathwick areas.",
    established: 1869,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Relocation"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Bathampton"],
    propertyCount: 65,
    avgPrice: "£650,000",
    rating: 4.4,
    reviewCount: 108,
    hours: [
      { days: "Mon–Fri", time: "08:45–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Hamptons has been a name in British property since 1869, and their Bath office on Quiet Street upholds that long-standing reputation. Positioned a short walk from the Royal Crescent, the office is well placed to serve the premium end of the Bath market, where Georgian townhouses and period apartments attract buyers from across the country.\n\nThe team draws on the Connells Group national buyer database, giving sellers access to a far wider pool of qualified purchasers than most independent agents can offer. Their relocation service is a particular draw, connecting incoming professionals with the right property in the right neighbourhood.\n\nWith 108 reviews and a 4.4 rating, the Bath team is known for market knowledge across Lansdown, Bathwick, and Widcombe. They also handle new-build properties, offering a rounded service for both traditional and contemporary homes.",
    whyChoose: [
      "Over 150 years of estate agency experience",
      "National buyer database through the Connells Group",
      "Relocation specialists for clients moving to Bath",
      "Rated 4.4/5 from 108 client reviews",
    ],
    faq: [
      {
        question: "What areas does Hamptons Bath cover?",
        answer:
          "Hamptons Bath covers Bath City Centre, Lansdown, Bathwick, Widcombe, and Bathampton. They have particular expertise in the premium neighbourhoods closest to the city's Georgian core.",
      },
      {
        question: "What services does Hamptons offer in Bath?",
        answer:
          "Hamptons Bath provides residential sales, lettings, new homes, and relocation services. They combine national marketing reach with detailed local market knowledge.",
      },
      {
        question: "What are Hamptons Bath's opening hours?",
        answer:
          "Hamptons Bath is open Monday to Friday from 08:45 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "jackson-stops-bath",
    name: "Jackson-Stops",
    branch: "Bath",
    address: "3 Miles's Buildings, George Street",
    postcode: "BA1 2QS",
    phone: "01225 425500",
    website: "https://www.jackson-stops.co.uk/branches/bath",
    email: "bath@jackson-stops.co.uk",
    description:
      "Jackson-Stops is a respected national estate agency with roots going back to 1910. Their Bath office on George Street serves the city and surrounding countryside, specialising in fine period houses, country estates, and premium property. Their team has extensive knowledge of the north-east Somerset and Wiltshire borders.",
    established: 1910,
    specialisms: ["Residential Sales", "Lettings", "Land & Development"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Bathford", "Bathampton", "Batheaston"],
    propertyCount: 45,
    avgPrice: "£785,000",
    rating: 4.6,
    reviewCount: 76,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Jackson-Stops has been a fixture of the country house market since 1910, and their Bath office on George Street brings that deep heritage to one of England's most architecturally significant cities. The team is especially well known for handling fine period houses, estates, and rural property along the north-east Somerset and Wiltshire borders.\n\nTheir focus on the villages of Bathford, Bathampton, and Batheaston sets them apart from agents who concentrate solely on the city centre. These areas attract buyers seeking countryside character with easy access to Bath's amenities, and Jackson-Stops' knowledge of this niche is hard to rival.\n\nWith a 4.6 rating from 76 reviews, the team has earned trust through careful, considered advice. They also offer land and development expertise, advising landowners on planning potential and site disposal across the region.",
    whyChoose: [
      "Over a century of country house and estate agency expertise",
      "Rated 4.6/5 from 76 client reviews",
      "Unrivalled knowledge of Bath's surrounding villages",
      "Land and development advisory for the wider region",
    ],
    faq: [
      {
        question: "What areas does Jackson-Stops Bath cover?",
        answer:
          "Jackson-Stops Bath covers Bath City Centre, Lansdown, Bathwick, Bathford, Bathampton, and Batheaston. They have particular strength in the rural villages on the north-east Somerset and Wiltshire borders.",
      },
      {
        question: "What services does Jackson-Stops offer in Bath?",
        answer:
          "Jackson-Stops Bath offers residential sales, lettings, and land and development services. They specialise in fine period houses, country estates, and premium residential property.",
      },
      {
        question: "What are Jackson-Stops Bath's opening hours?",
        answer:
          "Jackson-Stops Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "strutt-parker-bath",
    name: "Strutt & Parker",
    branch: "Bath",
    address: "5–6 Edgar Buildings, George Street",
    postcode: "BA1 2EE",
    phone: "01225 325995",
    website: "https://www.struttandparker.com/offices/bath",
    email: "bath@struttandparker.com",
    description:
      "Strutt & Parker's Bath office operates from the distinguished Edgar Buildings on George Street. Part of BNP Paribas Real Estate, they bring over 130 years of heritage to the Bath property market. Their team handles country houses, period residences, farms, and estates across Bath, the Cotswolds, and the wider south-west.",
    established: 1885,
    specialisms: ["Residential Sales", "Lettings", "Land & Development"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Bathford", "Bathampton", "Batheaston"],
    propertyCount: 40,
    avgPrice: "£895,000",
    rating: 4.5,
    reviewCount: 68,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Strutt & Parker was established in 1885 and their Bath office in Edgar Buildings on George Street reflects the firm's distinguished heritage. As part of BNP Paribas Real Estate, the team offers a rare combination of deep rural and country house expertise alongside the financial muscle of a major international property group.\n\nThe Bath office is best known for handling country houses, period residences, farms, and estates. Their reach extends well beyond the city into the Cotswolds and wider south-west, making them a natural choice for clients with rural property interests. Within Bath itself, they focus on the premium areas of Lansdown, Bathwick, and the surrounding villages.\n\nWith an average price of £895,000 and 68 client reviews at a 4.5 rating, Strutt & Parker Bath attracts clients who value measured, expert advice on significant property decisions. Their land and development team also advises on estates and farming assets.",
    whyChoose: [
      "Over 135 years of property and estate expertise",
      "Backed by BNP Paribas Real Estate's global network",
      "Country house and rural property specialists",
      "Rated 4.5/5 from 68 client reviews",
    ],
    faq: [
      {
        question: "What areas does Strutt & Parker Bath cover?",
        answer:
          "Strutt & Parker Bath covers Bath City Centre, Lansdown, Bathwick, Bathford, Bathampton, and Batheaston. Their reach extends into the Cotswolds and wider south-west for country and rural property.",
      },
      {
        question: "What services does Strutt & Parker offer in Bath?",
        answer:
          "Strutt & Parker Bath provides residential sales, lettings, and land and development services. They specialise in country houses, period residences, farms, and estate property.",
      },
      {
        question: "What are Strutt & Parker Bath's opening hours?",
        answer:
          "Strutt & Parker Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "allen-harris-bath",
    name: "Allen & Harris",
    branch: "Bath",
    address: "12 Quiet Street",
    postcode: "BA1 2JS",
    phone: "01225 461122",
    website: "https://www.allenandharris.co.uk/estate-agents/bath",
    email: "bath@allenandharris.co.uk",
    description:
      "Allen & Harris Bath is part of the Sequence national network, offering residential sales and lettings from their Quiet Street office. With access to a vast buyer database and decades of experience in the Bath market, they provide a dependable service covering properties from starter homes to Georgian townhouses across the city.",
    established: 1946,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Bath City Centre", "Widcombe", "Oldfield Park", "Bear Flat", "Weston", "Newbridge"],
    propertyCount: 110,
    avgPrice: "£420,000",
    rating: 4.1,
    reviewCount: 245,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–14:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Allen & Harris has served Bath's property market since 1946, building a dependable reputation across seven decades. Their Quiet Street office is part of the Sequence national network, giving sellers and landlords access to one of the UK's largest buyer and tenant databases.\n\nThe team handles a wide range of properties, from starter flats in Oldfield Park and Weston to period homes in Widcombe and Bear Flat. With 110 properties typically on their books and an average price of £420,000, they occupy the heart of the Bath market where most buyers and renters are looking.\n\nWith 245 client reviews, Allen & Harris Bath is one of the most-reviewed agents in the city. Their 4.1 rating reflects consistent, high-volume service backed by property management and new homes teams. For sellers seeking broad market exposure at a competitive fee, they remain a solid choice.",
    whyChoose: [
      "Established in Bath since 1946",
      "One of Bath's most-reviewed agents with 245 reviews",
      "Access to Sequence national buyer and tenant database",
      "Full service: sales, lettings, property management, and new homes",
    ],
    faq: [
      {
        question: "What areas does Allen & Harris Bath cover?",
        answer:
          "Allen & Harris Bath covers Bath City Centre, Widcombe, Oldfield Park, Bear Flat, Weston, and Newbridge. They handle properties across a broad range of Bath's residential neighbourhoods.",
      },
      {
        question: "What services does Allen & Harris offer in Bath?",
        answer:
          "Allen & Harris Bath offers residential sales, lettings, property management, and new homes services. They also provide mortgage and conveyancing referrals through the Sequence network.",
      },
      {
        question: "What are Allen & Harris Bath's opening hours?",
        answer:
          "Allen & Harris Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 14:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "andrews-bath",
    name: "Andrews",
    branch: "Bath",
    address: "14 Broad Street",
    postcode: "BA1 5LJ",
    phone: "01225 463434",
    website: "https://www.andrewsonline.co.uk/branches/bath",
    email: "bath@andrewsonline.co.uk",
    description:
      "Andrews has been a trusted name in the Bath property market since opening their city-centre branch on Broad Street. As a full-service agency, they cover sales, lettings, mortgages, conveyancing, and surveys. Their experienced team handles a broad range of properties across the city, from Georgian flats to family homes in the suburbs.",
    established: 1946,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Bath City Centre", "Larkhall", "Walcot", "Camden", "Weston", "Newbridge"],
    propertyCount: 120,
    avgPrice: "£395,000",
    rating: 4.2,
    reviewCount: 312,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–17:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Andrews is one of Bath's busiest and most visible estate agents, with their Broad Street office handling a high volume of transactions year after year. Since 1946, they have offered a genuinely full-service experience: sales, lettings, mortgages, conveyancing, and property surveys all under one roof.\n\nThe team covers a wide swathe of the city, from the Georgian streets of Walcot and Camden to the family-friendly neighbourhoods of Larkhall, Weston, and Newbridge. With 120 properties typically available and an average price of £395,000, they cater to first-time buyers, growing families, and investors alike.\n\nWith 312 client reviews (the highest count of any Bath agent), Andrews has proven their consistency over many years. Their 4.2 rating and Saturday opening until 17:00 make them one of the most accessible and well-known names in the city.",
    whyChoose: [
      "Bath's most-reviewed estate agent with 312 client reviews",
      "Full-service agency: sales, lettings, mortgages, surveys, and conveyancing",
      "Open Saturdays until 17:00 for maximum accessibility",
      "Established in 1946 with deep local roots",
    ],
    faq: [
      {
        question: "What areas does Andrews Bath cover?",
        answer:
          "Andrews Bath covers Bath City Centre, Larkhall, Walcot, Camden, Weston, and Newbridge. Their broad reach covers both the Georgian core and Bath's popular suburban neighbourhoods.",
      },
      {
        question: "What services does Andrews offer in Bath?",
        answer:
          "Andrews Bath is a full-service agency offering residential sales, lettings, property management, new homes, mortgages, conveyancing, and property surveys. They handle the entire transaction process in-house.",
      },
      {
        question: "What are Andrews Bath's opening hours?",
        answer:
          "Andrews Bath is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 17:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "winkworth-bath",
    name: "Winkworth",
    branch: "Bath",
    address: "3 Princes Buildings, George Street",
    postcode: "BA1 2ED",
    phone: "01225 829000",
    website: "https://www.winkworth.co.uk/estate-agents/bath",
    email: "bath@winkworth.co.uk",
    description:
      "Winkworth Bath is a franchise of the national Winkworth brand, operating from George Street in the city centre. Their team brings local expertise and a personal touch to residential sales and lettings, covering Bath, Batheaston, Bathford, and the surrounding countryside. They have a particular focus on period and character properties.",
    established: 1835,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Larkhall", "Batheaston"],
    propertyCount: 55,
    avgPrice: "£575,000",
    rating: 4.5,
    reviewCount: 98,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Winkworth traces its origins to 1835, making it one of the oldest estate agency brands in the country. The Bath franchise operates from Princes Buildings on George Street, where a locally owned team combines the strength of a national brand with the independence and personal accountability of a franchise model.\n\nTheir focus on period and character properties aligns naturally with Bath's architectural landscape. The team covers the Georgian heartland of Lansdown, Bathwick, and Widcombe, alongside the village feel of Larkhall and Batheaston. With 55 properties typically on their books, they maintain a curated, quality-focused portfolio.\n\nA 4.5 rating from 98 reviews speaks to a team that takes client relationships seriously. Sellers benefit from Winkworth's national brand recognition and cross-referral network, while buyers appreciate the local team's genuine knowledge of Bath's distinct neighbourhoods.",
    whyChoose: [
      "Part of the UK's oldest estate agency brand, established 1835",
      "Rated 4.5/5 from 98 client reviews",
      "Locally owned franchise with personal accountability",
      "Specialists in period and character properties across Bath",
    ],
    faq: [
      {
        question: "What areas does Winkworth Bath cover?",
        answer:
          "Winkworth Bath covers Bath City Centre, Lansdown, Bathwick, Widcombe, Larkhall, and Batheaston. They also handle properties in the surrounding countryside and nearby villages.",
      },
      {
        question: "What services does Winkworth offer in Bath?",
        answer:
          "Winkworth Bath offers residential sales and lettings. They focus on period and character properties, providing professional marketing, valuations, and buyer matching through their national network.",
      },
      {
        question: "What are Winkworth Bath's opening hours?",
        answer:
          "Winkworth Bath is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "cj-hole-bath",
    name: "CJ Hole",
    branch: "Bath",
    address: "19 Broad Street",
    postcode: "BA1 5LW",
    phone: "01225 444466",
    website: "https://www.cjhole.co.uk/estate-agents-and-letting-agents/branch/bath/",
    email: "bath@cjhole.co.uk",
    description:
      "CJ Hole Bath brings the heritage of one of the south-west's oldest estate agency brands to the city. From their Broad Street office, they handle sales and lettings across Bath and the surrounding area, offering a reliable, well-resourced service. Their team has strong knowledge of the local market from city-centre flats to suburban family homes.",
    established: 1867,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Bath City Centre", "Oldfield Park", "Bear Flat", "Twerton", "Weston", "Newbridge"],
    propertyCount: 95,
    avgPrice: "£365,000",
    rating: 4.3,
    reviewCount: 198,
    hours: [
      { days: "Mon–Fri", time: "08:45–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "CJ Hole has been part of the south-west property landscape since 1867, and their Bath office on Broad Street brings that deep regional heritage to a city where history matters. The team focuses on the practical, everyday end of the market: family homes in Oldfield Park, terraces in Twerton, and apartments in the city centre.\n\nWith 95 properties typically available and an average price of £365,000, they serve the core of Bath's residential market. Their property management division looks after a substantial lettings portfolio, making them a strong choice for landlords who want hands-on local management.\n\nThe 4.3 rating from 198 client reviews reflects steady, reliable performance across both sales and lettings. Their early opening at 08:45 and Saturday hours until 16:00 show a team that understands the working schedules of their clients.",
    whyChoose: [
      "Over 155 years of south-west estate agency heritage",
      "Rated 4.3/5 from 198 client reviews",
      "Strong property management and lettings portfolio",
      "Early opening and extended Saturday hours for convenience",
    ],
    faq: [
      {
        question: "What areas does CJ Hole Bath cover?",
        answer:
          "CJ Hole Bath covers Bath City Centre, Oldfield Park, Bear Flat, Twerton, Weston, and Newbridge. They focus on the city's residential suburbs and more affordable neighbourhoods.",
      },
      {
        question: "What services does CJ Hole offer in Bath?",
        answer:
          "CJ Hole Bath provides residential sales, lettings, and property management. They handle everything from marketing your home to full tenant management for landlords.",
      },
      {
        question: "What are CJ Hole Bath's opening hours?",
        answer:
          "CJ Hole Bath is open Monday to Friday from 08:45 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "fine-country-bath",
    name: "Fine & Country",
    branch: "Bath",
    address: "24 Milsom Street",
    postcode: "BA1 1DG",
    phone: "01225 320032",
    website: "https://www.fineandcountry.co.uk/bath-estate-agents",
    email: "bath@fineandcountry.com",
    description:
      "Fine & Country Bath specialises in premium and luxury property across the city and its rural surroundings. Their Milsom Street office showcases some of Bath's finest homes, with bespoke marketing including professional photography, video tours, and global reach through the Fine & Country international network.",
    established: 2003,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Relocation"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Bathampton", "Bathford"],
    propertyCount: 40,
    avgPrice: "£850,000",
    rating: 4.7,
    reviewCount: 72,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Fine & Country Bath brings luxury property marketing to the World Heritage City from their prestigious Milsom Street address. Since 2003, they have carved out a distinct position in Bath's premium market, where bespoke presentation and international exposure make a tangible difference to sale outcomes.\n\nEvery instruction receives professional photography, video tours, and lifestyle-led marketing that positions properties as more than bricks and mortar. Their international network connects Bath's finest homes with buyers across Europe, the Middle East, and beyond, while their relocation service helps incoming clients settle into the right neighbourhood.\n\nWith a 4.7 rating from 72 reviews, the team has earned a reputation for polished, results-driven service. An average property price of £850,000 reflects their focus on the upper market, covering Lansdown, Bathwick, Widcombe, and the villages of Bathampton and Bathford.",
    whyChoose: [
      "Rated 4.7/5 from 72 client reviews",
      "Bespoke marketing with professional photography and video tours",
      "International buyer network spanning multiple continents",
      "Relocation service for clients moving to Bath",
    ],
    faq: [
      {
        question: "What areas does Fine & Country Bath cover?",
        answer:
          "Fine & Country Bath covers Bath City Centre, Lansdown, Bathwick, Widcombe, Bathampton, and Bathford. Their international network also attracts interest from buyers outside the region.",
      },
      {
        question: "What services does Fine & Country offer in Bath?",
        answer:
          "Fine & Country Bath offers residential sales, lettings, new homes, and relocation services. Every property receives bespoke marketing including professional photography, video tours, and international exposure.",
      },
      {
        question: "What are Fine & Country Bath's opening hours?",
        answer:
          "Fine & Country Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "havilands-bath",
    name: "Havilands",
    branch: "Bath",
    address: "17 Manvers Street",
    postcode: "BA1 1JW",
    phone: "01225 428888",
    website: "https://www.havilands.com",
    email: "info@havilands.com",
    description:
      "Havilands is a well-established independent estate and letting agent based in Bath, with over two decades serving the city. From their office near Bath Spa station, they manage a large portfolio of rental properties alongside residential sales. Known for their proactive approach and genuine local knowledge across every Bath postcode.",
    established: 2000,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Bath City Centre", "Widcombe", "Oldfield Park", "Bear Flat", "Combe Down", "Odd Down"],
    propertyCount: 145,
    avgPrice: "£380,000",
    rating: 4.4,
    reviewCount: 267,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–14:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Havilands has grown steadily since 2000 to become one of Bath's most active independent agents, with 145 properties typically on their books. Their Manvers Street office, just steps from Bath Spa station, is ideally placed for clients who need a central, accessible base for property viewings and management.\n\nThe team's strength lies in lettings and property management, where they oversee a substantial rental portfolio across Widcombe, Oldfield Park, Bear Flat, Combe Down, and Odd Down. Their proactive approach to maintenance and tenant relations has made them a first-choice landlord agent across south Bath.\n\nOn the sales side, an average price of £380,000 positions them firmly in the mainstream market. With 267 client reviews and a 4.4 rating, Havilands has built genuine trust through consistent, hands-on service that larger corporate agents often struggle to replicate.",
    whyChoose: [
      "Rated 4.4/5 from 267 client reviews",
      "One of Bath's largest independent lettings portfolios",
      "Conveniently located near Bath Spa station",
      "Over two decades of independent Bath market expertise",
    ],
    faq: [
      {
        question: "What areas does Havilands Bath cover?",
        answer:
          "Havilands covers Bath City Centre, Widcombe, Oldfield Park, Bear Flat, Combe Down, and Odd Down. They have particularly strong lettings coverage across south Bath.",
      },
      {
        question: "What services does Havilands offer in Bath?",
        answer:
          "Havilands offers residential sales, lettings, and property management. Their managed lettings service is especially well regarded, covering tenant sourcing, maintenance, and full portfolio oversight.",
      },
      {
        question: "What are Havilands Bath's opening hours?",
        answer:
          "Havilands is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 14:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "pritchards-bath",
    name: "Pritchards",
    branch: "Bath",
    address: "4 Bridge Street",
    postcode: "BA2 4AS",
    phone: "01225 422211",
    website: "https://www.pritchards-bath.co.uk",
    email: "info@pritchards-bath.co.uk",
    description:
      "Pritchards is a family-run, independent estate agent in Bath with an excellent reputation for personal service. Their Bridge Street office, near Pulteney Bridge, handles residential sales across Bath and the surrounding villages. With a focus on quality over quantity, their small team provides thoughtful, tailored advice to buyers and sellers alike.",
    established: 1990,
    specialisms: ["Residential Sales", "Land & Development"],
    areas: ["Bath City Centre", "Bathwick", "Widcombe", "Lansdown", "Camden", "Walcot"],
    propertyCount: 35,
    avgPrice: "£595,000",
    rating: 4.9,
    reviewCount: 89,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Pritchards is a family-run agency that has quietly earned one of the strongest reputations in Bath since 1990. Their office on Bridge Street, overlooking Pulteney Bridge, is a fitting home for an agent whose strength lies in the Georgian core of the city: Bathwick, Lansdown, Camden, and Walcot.\n\nWith just 35 properties typically on their books, Pritchards takes a deliberate approach to business. Every instruction receives the full attention of a small, experienced team that prioritises honest advice and realistic pricing over volume. Their land and development expertise also serves private clients looking to unlock value from gardens, plots, and redundant buildings.\n\nA 4.9 rating from 89 reviews makes Pritchards the highest-rated agent in Bath. That score reflects a client base that values personal relationships and expert local knowledge over the scale and marketing budgets of larger firms.",
    whyChoose: [
      "Bath's highest-rated agent at 4.9/5 from 89 reviews",
      "Family-run and independent since 1990",
      "Quality-focused with tailored advice for every client",
      "Land and development expertise for private clients",
    ],
    faq: [
      {
        question: "What areas does Pritchards Bath cover?",
        answer:
          "Pritchards covers Bath City Centre, Bathwick, Widcombe, Lansdown, Camden, and Walcot. They focus on the Georgian heart of the city and its most characterful neighbourhoods.",
      },
      {
        question: "What services does Pritchards offer in Bath?",
        answer:
          "Pritchards specialises in residential sales and land and development services. They provide honest valuations, professional marketing, and tailored advice for buyers and sellers of distinctive Bath properties.",
      },
      {
        question: "What are Pritchards Bath's opening hours?",
        answer:
          "Pritchards is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "connells-bath",
    name: "Connells",
    branch: "Bath",
    address: "20 Southgate Street",
    postcode: "BA1 1AQ",
    phone: "01225 444445",
    website: "https://www.connells.co.uk/estate-agents/bath",
    email: "bath@connells.co.uk",
    description:
      "Connells Bath is part of the UK's largest estate agency group, with their Southgate Street office serving as a busy hub for residential sales and lettings. Their team handles a high volume of transactions across Bath, from starter flats in Oldfield Park to period homes in Lansdown, backed by a national buyer database.",
    established: 1936,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Bath City Centre", "Oldfield Park", "Bear Flat", "Combe Down", "Odd Down", "Twerton"],
    propertyCount: 130,
    avgPrice: "£355,000",
    rating: 4.0,
    reviewCount: 278,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Connells is the UK's largest estate agency group, and their Bath office on Southgate Street handles one of the highest transaction volumes in the city. Founded in 1936, the brand brings nearly nine decades of experience to a market where affordability and accessibility matter just as much as heritage.\n\nThe Bath team focuses on the city's more affordable neighbourhoods: Oldfield Park, Bear Flat, Combe Down, Odd Down, and Twerton. With 130 properties on their books and an average price of £355,000, they are a natural starting point for first-time buyers, young families, and buy-to-let investors.\n\nTheir 278 client reviews demonstrate sheer volume of activity, while their property management and new homes divisions add depth to the core sales and lettings service. For sellers who want maximum market exposure at a competitive price point, Connells delivers consistent results.",
    whyChoose: [
      "Part of the UK's largest estate agency group",
      "Over 278 client reviews demonstrating high transaction volume",
      "Specialists in Bath's most accessible price brackets",
      "Full service: sales, lettings, property management, and new homes",
    ],
    faq: [
      {
        question: "What areas does Connells Bath cover?",
        answer:
          "Connells Bath covers Bath City Centre, Oldfield Park, Bear Flat, Combe Down, Odd Down, and Twerton. They focus on Bath's more affordable residential neighbourhoods.",
      },
      {
        question: "What services does Connells offer in Bath?",
        answer:
          "Connells Bath provides residential sales, lettings, property management, and new homes services. They also offer mortgage and conveyancing referrals through their national network.",
      },
      {
        question: "What are Connells Bath's opening hours?",
        answer:
          "Connells Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "romans-bath",
    name: "Romans",
    branch: "Bath",
    address: "16 Milsom Street",
    postcode: "BA1 1DG",
    phone: "01225 484999",
    website: "https://www.romans.co.uk/estate-lettings-agents/bath",
    email: "bath@romans.co.uk",
    description:
      "Romans Bath occupies a prime Milsom Street location and is a well-known presence in the city's property market. Established in 1987, they offer residential sales, lettings, buy-to-let advice, and property surveys. Their strength lies in the lettings market, where they manage a substantial portfolio of rental properties across Bath.",
    established: 1987,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "Relocation"],
    areas: ["Bath City Centre", "Widcombe", "Oldfield Park", "Bear Flat", "Walcot", "Camden"],
    propertyCount: 100,
    avgPrice: "£1,350/month",
    rating: 4.3,
    reviewCount: 186,
    hours: [
      { days: "Mon–Thu", time: "08:45–18:00" },
      { days: "Fri", time: "08:45–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Romans has been a prominent name on Bath's Milsom Street since 1987, building a particularly strong position in the city's lettings market. Their managed rental portfolio is one of the largest in Bath, serving landlords and tenants across Widcombe, Oldfield Park, Bear Flat, Walcot, and Camden.\n\nBeyond lettings, the team handles residential sales and offers buy-to-let investment advice, property surveys, and a relocation service for professionals moving to the area. This breadth of service under one roof makes them especially useful for landlords who want end-to-end support.\n\nWith 186 client reviews and a 4.3 rating, Romans Bath benefits from nearly four decades of local market data and relationships. Their early opening at 08:45 is a small but telling detail; it reflects a team that works to their clients' schedules rather than the other way around.",
    whyChoose: [
      "Bath's leading lettings specialist since 1987",
      "Rated 4.3/5 from 186 client reviews",
      "Buy-to-let advice and property survey services",
      "Relocation support for professionals moving to Bath",
    ],
    faq: [
      {
        question: "What areas does Romans Bath cover?",
        answer:
          "Romans Bath covers Bath City Centre, Widcombe, Oldfield Park, Bear Flat, Walcot, and Camden. Their lettings portfolio extends across all of these neighbourhoods.",
      },
      {
        question: "What services does Romans offer in Bath?",
        answer:
          "Romans Bath provides residential sales, lettings, property management, and relocation services. They also offer buy-to-let investment advice and property surveys.",
      },
      {
        question: "What are Romans Bath's opening hours?",
        answer:
          "Romans Bath is open Monday to Thursday from 08:45 to 18:00, Friday from 08:45 to 17:30, and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "imagine-property-bath",
    name: "Imagine Property Group",
    branch: "Bath",
    address: "30 Monmouth Street",
    postcode: "BA1 2AP",
    phone: "01225 331199",
    website: "https://www.imaginepropertygroup.co.uk",
    email: "info@imaginepropertygroup.co.uk",
    description:
      "Imagine Property Group is a modern, independent estate agent and letting agency in the heart of Bath. Their approach combines contemporary digital marketing with hands-on local service, covering sales and lettings across the city and surrounding areas. They have a particular strength in the student and professional rental markets.",
    established: 2012,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Bath City Centre", "Oldfield Park", "Widcombe", "Bear Flat", "Larkhall", "Walcot"],
    propertyCount: 85,
    avgPrice: "£375,000",
    rating: 4.6,
    reviewCount: 134,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Imagine Property Group launched in 2012 with a clear ambition: to bring modern, digitally-led marketing to Bath's property market without losing the personal service that clients rightly expect. Their Monmouth Street office has since become a well-regarded name across both sales and lettings.\n\nThe team has particular strength in Bath's student and professional rental markets, managing properties across Oldfield Park, Widcombe, Bear Flat, Larkhall, and Walcot. Their digital-first approach to marketing means properties gain early traction online, while their local knowledge ensures accurate pricing and well-matched tenants.\n\nWith 134 client reviews and a 4.6 rating, Imagine Property Group has won loyalty quickly for a relatively young agency. Their blend of tech-savvy marketing and genuine hands-on service appeals to a generation of landlords and sellers who expect both efficiency and a human touch.",
    whyChoose: [
      "Rated 4.6/5 from 134 client reviews",
      "Modern digital marketing with hands-on local service",
      "Specialists in student and professional rental markets",
      "Independent agency with a personal, responsive approach",
    ],
    faq: [
      {
        question: "What areas does Imagine Property Group Bath cover?",
        answer:
          "Imagine Property Group covers Bath City Centre, Oldfield Park, Widcombe, Bear Flat, Larkhall, and Walcot. They are especially active in areas popular with students and young professionals.",
      },
      {
        question: "What services does Imagine Property Group offer in Bath?",
        answer:
          "Imagine Property Group provides residential sales, lettings, and property management. Their digital marketing approach ensures strong online visibility for every property they handle.",
      },
      {
        question: "What are Imagine Property Group Bath's opening hours?",
        answer:
          "Imagine Property Group is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "simply-residential-bath",
    name: "Simply Residential",
    branch: "Bath",
    address: "4 Gay Street",
    postcode: "BA1 2PH",
    phone: "01225 335533",
    website: "https://www.simplyresidential.co.uk",
    email: "info@simplyresidential.co.uk",
    description:
      "Simply Residential is an independent Bath estate agent offering a refreshingly straightforward approach to selling and letting property. From their Gay Street office between the Circus and Queen Square, they provide honest valuations, professional photography, and genuine local expertise across the city.",
    established: 2008,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Camden", "Walcot"],
    propertyCount: 40,
    avgPrice: "£525,000",
    rating: 4.8,
    reviewCount: 96,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Simply Residential has offered Bath sellers and landlords a refreshingly honest service since 2008. Their Gay Street office sits between the Circus and Queen Square, in the heart of the Georgian city they know inside out. The name says it all: no gimmicks, no jargon, just clear advice backed by genuine local expertise.\n\nThe team handles residential sales and lettings across Bath's most desirable central neighbourhoods: Lansdown, Bathwick, Widcombe, Camden, and Walcot. With 40 properties on their books and an average price of £525,000, they occupy a carefully chosen middle ground between the high-end nationals and the volume-driven chains.\n\nA 4.8 rating from 96 reviews places Simply Residential among Bath's top-rated agents. Clients consistently highlight their honest valuations, clear communication, and professional photography, the kind of details that make a real difference when selling a distinctive home.",
    whyChoose: [
      "Rated 4.8/5 from 96 client reviews",
      "Straightforward, jargon-free approach to property sales",
      "Independent agency in the heart of Georgian Bath",
      "Professional photography and honest valuations as standard",
    ],
    faq: [
      {
        question: "What areas does Simply Residential Bath cover?",
        answer:
          "Simply Residential covers Bath City Centre, Lansdown, Bathwick, Widcombe, Camden, and Walcot. Their focus is on the Georgian core of the city and its surrounding central neighbourhoods.",
      },
      {
        question: "What services does Simply Residential offer in Bath?",
        answer:
          "Simply Residential offers residential sales and lettings. Every instruction includes professional photography, honest valuations, and proactive marketing through their website and major property portals.",
      },
      {
        question: "What are Simply Residential Bath's opening hours?",
        answer:
          "Simply Residential is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "fox-sons-bath",
    name: "Fox & Sons",
    branch: "Bath",
    address: "2 Southgate Street",
    postcode: "BA1 1AQ",
    phone: "01225 460999",
    website: "https://www.fox-and-sons.co.uk/estate-agents/bath",
    email: "bath@fox-and-sons.co.uk",
    description:
      "Fox & Sons Bath is part of one of the south's most recognisable estate agency brands. Their Southgate Street office handles residential sales and lettings across Bath, with the backing of the Connells Group national network. Their team covers properties from city-centre apartments to suburban family homes.",
    established: 1878,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Bath City Centre", "Oldfield Park", "Bear Flat", "Combe Down", "Odd Down", "Twerton"],
    propertyCount: 90,
    avgPrice: "£385,000",
    rating: 4.2,
    reviewCount: 176,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Sat", time: "09:00–17:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Fox & Sons is one of the south of England's most recognisable estate agency brands, with roots stretching back to 1878. Their Bath office on Southgate Street benefits from the Connells Group national network, which provides access to a vast buyer and tenant database alongside substantial marketing resources.\n\nThe team covers the practical side of the Bath market: Oldfield Park, Bear Flat, Combe Down, Odd Down, and Twerton. With 90 properties on their books and an average price of £385,000, they serve families, first-time buyers, and investors who need reliable, knowledgeable service without premium pricing.\n\nWith 176 client reviews and a 4.2 rating, Fox & Sons Bath delivers consistent results. Their early 08:30 opening and Saturday hours until 17:00 offer maximum flexibility, and their property management and new homes teams provide additional depth for landlords and developers.",
    whyChoose: [
      "Over 145 years of estate agency heritage",
      "Rated 4.2/5 from 176 client reviews",
      "Early opening at 08:30 and Saturday hours until 17:00",
      "Full service: sales, lettings, property management, and new homes",
    ],
    faq: [
      {
        question: "What areas does Fox & Sons Bath cover?",
        answer:
          "Fox & Sons Bath covers Bath City Centre, Oldfield Park, Bear Flat, Combe Down, Odd Down, and Twerton. They focus on Bath's residential suburbs and more affordable areas.",
      },
      {
        question: "What services does Fox & Sons offer in Bath?",
        answer:
          "Fox & Sons Bath provides residential sales, lettings, property management, and new homes services. They are backed by the Connells Group national network for wide buyer reach.",
      },
      {
        question: "What are Fox & Sons Bath's opening hours?",
        answer:
          "Fox & Sons Bath is open Monday to Friday from 08:30 to 18:00 and Saturday from 09:00 to 17:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "pulteney-estates-bath",
    name: "Pulteney Estates",
    branch: "Bath",
    address: "7 Argyle Street, Bathwick",
    postcode: "BA2 4BA",
    phone: "01225 463500",
    website: "https://www.pulteneyestates.co.uk",
    email: "info@pulteneyestates.co.uk",
    description:
      "Pulteney Estates is a boutique independent estate agent in the heart of Bathwick, moments from Pulteney Bridge. Specialising in the Georgian core and surrounding areas, they offer a personal, knowledgeable service to sellers and buyers of distinctive Bath properties. Their intimate team means every client receives individual attention.",
    established: 2005,
    specialisms: ["Residential Sales"],
    areas: ["Bathwick", "Bath City Centre", "Widcombe", "Lansdown", "Camden"],
    propertyCount: 25,
    avgPrice: "£650,000",
    rating: 4.9,
    reviewCount: 64,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "10:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Pulteney Estates is a true boutique agency, operating from Argyle Street in Bathwick just moments from the iconic Pulteney Bridge. Since 2005, their small team has focused exclusively on residential sales in Bath's Georgian core, earning one of the highest client ratings in the city.\n\nWith only 25 properties typically on their books, every listing receives a level of personal attention that larger agencies simply cannot replicate. The team knows Bathwick, Widcombe, Lansdown, and Camden street by street, and their understanding of period architecture helps sellers present their homes to best advantage.\n\nA 4.9 rating from 64 reviews places Pulteney Estates alongside Pritchards as Bath's highest-rated agents. Their deliberately small scale is a feature, not a limitation. Clients choose them precisely because they want an agent who treats every sale as if it were their only one.",
    whyChoose: [
      "Joint highest-rated Bath agent at 4.9/5 from 64 reviews",
      "Boutique agency with individual attention for every client",
      "Deep expertise in Bath's Georgian core and Bathwick",
      "Focused exclusively on residential sales since 2005",
    ],
    faq: [
      {
        question: "What areas does Pulteney Estates Bath cover?",
        answer:
          "Pulteney Estates covers Bathwick, Bath City Centre, Widcombe, Lansdown, and Camden. They specialise in the Georgian heart of the city and its most architecturally distinctive neighbourhoods.",
      },
      {
        question: "What services does Pulteney Estates offer in Bath?",
        answer:
          "Pulteney Estates focuses exclusively on residential sales. Their boutique approach means every property receives personal marketing, considered pricing, and hands-on support throughout the process.",
      },
      {
        question: "What are Pulteney Estates Bath's opening hours?",
        answer:
          "Pulteney Estates is open Monday to Friday from 09:00 to 17:30 and Saturday from 10:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "bidmead-cook-bath",
    name: "Bidmead Cook",
    branch: "Bath",
    address: "6 Terrace Walk",
    postcode: "BA1 1LN",
    phone: "01225 483838",
    website: "https://www.bidmeadcook.co.uk",
    email: "bath@bidmeadcook.co.uk",
    description:
      "Bidmead Cook is a respected independent estate and letting agent with offices in Bath and Midsomer Norton. Their Bath office on Terrace Walk covers sales and lettings across the city and the surrounding north-east Somerset area. With over 20 years of local experience, they offer a professional yet personal service.",
    established: 1997,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "Auctions"],
    areas: ["Bath City Centre", "Widcombe", "Combe Down", "Odd Down", "Bear Flat", "Oldfield Park"],
    propertyCount: 80,
    avgPrice: "£410,000",
    rating: 4.5,
    reviewCount: 178,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Bidmead Cook has been serving Bath's property market since 1997, with offices in both the city centre and Midsomer Norton. Their Terrace Walk office, tucked behind the Abbey, handles sales, lettings, and property management across Bath and the wider north-east Somerset area.\n\nWhat sets Bidmead Cook apart is their auction service, a genuine differentiator in a market where most agents focus solely on private treaty sales. For sellers with unusual properties, those needing a quick completion, or investors seeking opportunities, their regular auctions provide a valuable alternative route to market.\n\nWith 178 client reviews and a 4.5 rating, the team has built a loyal client base across Widcombe, Combe Down, Odd Down, Bear Flat, and Oldfield Park. Their property management division oversees a substantial rental portfolio, giving landlords confidence in a team that understands both sides of the market.",
    whyChoose: [
      "Rated 4.5/5 from 178 client reviews",
      "One of few Bath agents offering a dedicated auction service",
      "Independent with over 25 years of local experience",
      "Two offices covering Bath and Midsomer Norton",
    ],
    faq: [
      {
        question: "What areas does Bidmead Cook Bath cover?",
        answer:
          "Bidmead Cook Bath covers Bath City Centre, Widcombe, Combe Down, Odd Down, Bear Flat, and Oldfield Park. Their Midsomer Norton office extends coverage into the wider north-east Somerset area.",
      },
      {
        question: "What services does Bidmead Cook offer in Bath?",
        answer:
          "Bidmead Cook provides residential sales, lettings, property management, and auctions. Their auction service is particularly distinctive, offering an alternative route to market for certain property types.",
      },
      {
        question: "What are Bidmead Cook Bath's opening hours?",
        answer:
          "Bidmead Cook Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "chancellors-bath",
    name: "Chancellors",
    branch: "Bath",
    address: "9a Broad Street",
    postcode: "BA1 5LJ",
    phone: "01225 335566",
    website: "https://www.chancellors.co.uk/estate-agents/bath",
    email: "bath@chancellors.co.uk",
    description:
      "Chancellors Bath is part of a well-known regional chain covering the Thames Valley and south-west. Their Broad Street office handles sales and lettings across the city, with a strong focus on family homes and investment properties. Their team provides a reliable, high-volume service backed by extensive regional expertise.",
    established: 1990,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Bath City Centre", "Oldfield Park", "Bear Flat", "Weston", "Newbridge", "Larkhall"],
    propertyCount: 105,
    avgPrice: "£390,000",
    rating: 4.2,
    reviewCount: 215,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Chancellors is a regional chain with a strong presence across the Thames Valley and south-west, and their Bath office on Broad Street has been serving the city since 1990. The team handles a high volume of sales and lettings, with a particular focus on family homes in Oldfield Park, Bear Flat, Weston, Newbridge, and Larkhall.\n\nWith 105 properties typically available and an average price of £390,000, Chancellors sits at the heart of the mainstream Bath market. Their regional network means sellers benefit from cross-referrals between offices, while their property management team offers a complete landlord service.\n\nA 4.2 rating from 215 reviews reflects consistent, dependable performance. Their extended Saturday hours until 16:00 and weekday opening until 18:00 show a team oriented towards accessibility and convenience for working buyers and sellers.",
    whyChoose: [
      "Regional network covering the Thames Valley and south-west",
      "Rated 4.2/5 from 215 client reviews",
      "Specialists in family homes and investment properties",
      "Extended opening hours including Saturdays until 16:00",
    ],
    faq: [
      {
        question: "What areas does Chancellors Bath cover?",
        answer:
          "Chancellors Bath covers Bath City Centre, Oldfield Park, Bear Flat, Weston, Newbridge, and Larkhall. They focus on Bath's popular family neighbourhoods and suburban areas.",
      },
      {
        question: "What services does Chancellors offer in Bath?",
        answer:
          "Chancellors Bath provides residential sales, lettings, and property management. Their regional network also supports cross-referrals between offices across the Thames Valley and south-west.",
      },
      {
        question: "What are Chancellors Bath's opening hours?",
        answer:
          "Chancellors Bath is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "your-move-bath",
    name: "Your Move",
    branch: "Bath",
    address: "22 Southgate Street",
    postcode: "BA1 1AQ",
    phone: "01225 484588",
    website: "https://www.your-move.co.uk/estate-agent/bath",
    email: "bath@your-move.co.uk",
    description:
      "Your Move Bath is part of one of the UK's largest estate agency networks, located on Southgate Street in the city centre. They offer a comprehensive range of services including sales, lettings, mortgages, and conveyancing. With national marketing reach and local market knowledge, they cater to all property price ranges across Bath.",
    established: 1982,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Bath City Centre", "Oldfield Park", "Bear Flat", "Twerton", "Weston", "Odd Down"],
    propertyCount: 115,
    avgPrice: "£350,000",
    rating: 4.0,
    reviewCount: 234,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Your Move has been one of the UK's most recognised estate agency brands since 1982, and their Bath office on Southgate Street handles a broad spectrum of residential sales and lettings. Their strength is accessibility: competitive fees, straightforward service, and in-house mortgages and conveyancing that streamline the buying process.\n\nThe team covers Bath's more affordable neighbourhoods, including Oldfield Park, Bear Flat, Twerton, Weston, and Odd Down. With 115 properties on their books and an average price of £350,000, they cater largely to first-time buyers, investors, and families looking for value in the Bath market.\n\nWith 234 client reviews, Your Move Bath is one of the city's most active agents by volume. Their 4.0 rating and Saturday opening until 16:00 reflect a practical, results-driven approach that prioritises getting properties sold and tenancies filled efficiently.",
    whyChoose: [
      "Part of one of the UK's largest estate agency networks",
      "In-house mortgages and conveyancing for a streamlined process",
      "Over 234 client reviews demonstrating high activity",
      "Competitive fees across all Bath price ranges",
    ],
    faq: [
      {
        question: "What areas does Your Move Bath cover?",
        answer:
          "Your Move Bath covers Bath City Centre, Oldfield Park, Bear Flat, Twerton, Weston, and Odd Down. They cater to all price ranges across Bath's residential neighbourhoods.",
      },
      {
        question: "What services does Your Move offer in Bath?",
        answer:
          "Your Move Bath provides residential sales, lettings, property management, and new homes services. They also offer in-house mortgage advice and conveyancing referrals.",
      },
      {
        question: "What are Your Move Bath's opening hours?",
        answer:
          "Your Move Bath is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "marsh-parsons-bath",
    name: "Marsh & Parsons",
    branch: "Bath",
    address: "1 Edgar Buildings, George Street",
    postcode: "BA1 2EE",
    phone: "01225 325900",
    website: "https://www.marshandparsons.co.uk/branches/bath",
    email: "bath@marshandparsons.co.uk",
    description:
      "Marsh & Parsons' Bath office brings London-grade marketing standards to the World Heritage City. From their George Street base, they handle premium residential sales and lettings with an emphasis on stylish property presentation and proactive buyer matching. Part of the Dexters family, they attract London-based buyers looking to relocate to Bath.",
    established: 1856,
    specialisms: ["Residential Sales", "Lettings", "Relocation"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Camden"],
    propertyCount: 45,
    avgPrice: "£725,000",
    rating: 4.6,
    reviewCount: 82,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:30–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Marsh & Parsons brings a distinctly London-polished approach to Bath's property market, operating from Edgar Buildings on George Street. Part of the Dexters family, they are one of the few agents in Bath with genuine, active London connections, regularly matching Bath properties with buyers looking to leave the capital.\n\nTheir marketing is notably stylish: professional photography, well-crafted descriptions, and a presentation standard that reflects their London heritage. The team focuses on Bath City Centre, Lansdown, Bathwick, Widcombe, and Camden, handling premium residential sales and lettings with a proactive, buyer-led approach.\n\nWith a 4.6 rating from 82 reviews and an average price of £725,000, Marsh & Parsons serves the upper-middle tier of the Bath market. Their relocation service is a genuine strength, providing end-to-end support for professionals and families making the move from London to Bath.",
    whyChoose: [
      "Active London buyer connections through the Dexters network",
      "Rated 4.6/5 from 82 client reviews",
      "Stylish, London-grade property marketing and presentation",
      "Dedicated relocation service for London-to-Bath movers",
    ],
    faq: [
      {
        question: "What areas does Marsh & Parsons Bath cover?",
        answer:
          "Marsh & Parsons Bath covers Bath City Centre, Lansdown, Bathwick, Widcombe, and Camden. They specialise in the premium central neighbourhoods that attract relocating London buyers.",
      },
      {
        question: "What services does Marsh & Parsons offer in Bath?",
        answer:
          "Marsh & Parsons Bath provides residential sales, lettings, and relocation services. Their marketing includes professional photography and stylish presentation tailored to premium properties.",
      },
      {
        question: "What are Marsh & Parsons Bath's opening hours?",
        answer:
          "Marsh & Parsons Bath is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:30 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "chappell-matthews-bath",
    name: "Chappell & Matthews",
    branch: "Bath",
    address: "11 George Street",
    postcode: "BA1 2EH",
    phone: "01225 330055",
    website: "https://www.chappellandmatthews.co.uk",
    email: "bath@chappellandmatthews.co.uk",
    description:
      "Chappell & Matthews has been helping discerning clients since 1913, and their Bath office on George Street brings over a century of property expertise to the city. They combine traditional values with contemporary marketing, handling sales, lettings, and property management across Bath and the surrounding area.",
    established: 1913,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Bath City Centre", "Lansdown", "Bathwick", "Widcombe", "Larkhall", "Camden"],
    propertyCount: 70,
    avgPrice: "£540,000",
    rating: 4.5,
    reviewCount: 126,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Chappell & Matthews has been in the property business since 1913, and their George Street office brings over a century of accumulated market knowledge to Bath. This heritage lends a certain confidence to their advice, grounded in traditional values but delivered with contemporary marketing savvy.\n\nThe team handles sales, lettings, and property management across Bath's most appealing central areas: Lansdown, Bathwick, Widcombe, Larkhall, and Camden. With 70 properties typically on their books and an average price of £540,000, they serve a discerning client base that values experience and a considered approach.\n\nA 4.5 rating from 126 reviews reflects steady, quality-driven service. Their longevity in the market means deep relationships with local solicitors, surveyors, and mortgage brokers, connections that can smooth the path from offer to completion.",
    whyChoose: [
      "Over a century of property expertise since 1913",
      "Rated 4.5/5 from 126 client reviews",
      "Traditional values with contemporary marketing approach",
      "Sales, lettings, and property management under one roof",
    ],
    faq: [
      {
        question: "What areas does Chappell & Matthews Bath cover?",
        answer:
          "Chappell & Matthews Bath covers Bath City Centre, Lansdown, Bathwick, Widcombe, Larkhall, and Camden. They focus on the city's most characterful central and inner residential areas.",
      },
      {
        question: "What services does Chappell & Matthews offer in Bath?",
        answer:
          "Chappell & Matthews provides residential sales, lettings, and property management. They combine over a century of market knowledge with modern marketing and property presentation.",
      },
      {
        question: "What are Chappell & Matthews Bath's opening hours?",
        answer:
          "Chappell & Matthews Bath is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "aquarius-bath",
    name: "Aquarius Property",
    branch: "Bath",
    address: "15 Monmouth Street",
    postcode: "BA1 2AP",
    phone: "01225 331166",
    website: "https://www.aquariusproperty.co.uk",
    email: "info@aquariusproperty.co.uk",
    description:
      "Aquarius Property is an independent Bath lettings and property management specialist, managing a large portfolio of rental properties across the city. Their Monmouth Street office is a go-to for landlords and tenants alike, with a hands-on approach and deep understanding of Bath's diverse rental market.",
    established: 2001,
    specialisms: ["Lettings", "Property Management"],
    areas: ["Bath City Centre", "Oldfield Park", "Widcombe", "Bear Flat", "Larkhall", "Walcot"],
    propertyCount: 180,
    avgPrice: "£1,250/month",
    rating: 4.3,
    reviewCount: 156,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–12:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Aquarius Property has been Bath's dedicated lettings and property management specialist since 2001. Unlike most agents who split their attention between sales and lettings, Aquarius focuses entirely on the rental market, and it shows in the depth of their service and the size of their portfolio.\n\nWith 180 managed properties and an average rent of £1,250 per month, they are one of Bath's largest lettings operations. Their team covers the city's key rental areas: Oldfield Park, Widcombe, Bear Flat, Larkhall, and Walcot, serving everyone from university students to professional tenants and corporate relocations.\n\nA 4.3 rating from 156 reviews reflects their standing among both landlords and tenants. Their hands-on approach to maintenance, regular property inspections, and responsive communication make them a trusted choice for landlords who want their properties well cared for.",
    whyChoose: [
      "Bath's dedicated lettings and property management specialist",
      "One of the city's largest rental portfolios with 180 properties",
      "Rated 4.3/5 from 156 client reviews",
      "Over two decades of focused lettings expertise since 2001",
    ],
    faq: [
      {
        question: "What areas does Aquarius Property Bath cover?",
        answer:
          "Aquarius Property covers Bath City Centre, Oldfield Park, Widcombe, Bear Flat, Larkhall, and Walcot. They have particularly strong rental market knowledge in Oldfield Park and the city centre.",
      },
      {
        question: "What services does Aquarius Property offer in Bath?",
        answer:
          "Aquarius Property specialises exclusively in lettings and property management. They do not handle sales. Their services include tenant sourcing, rent collection, property inspections, and full maintenance management.",
      },
      {
        question: "What are Aquarius Property Bath's opening hours?",
        answer:
          "Aquarius Property is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 12:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "combe-residential-bath",
    name: "Combe Residential",
    branch: "Combe Down",
    address: "1 The Avenue, Combe Down",
    postcode: "BA2 5EH",
    phone: "01225 836400",
    website: "https://www.comberesidential.co.uk",
    email: "info@comberesidential.co.uk",
    description:
      "Combe Residential is a well-regarded independent estate agent based in Combe Down on the southern edge of Bath. They specialise in properties across the southern Bath villages including Combe Down, Odd Down, and Bear Flat, offering an intimate local service that larger city-centre agents often cannot match.",
    established: 2010,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Combe Down", "Odd Down", "Bear Flat", "Widcombe", "Oldfield Park"],
    propertyCount: 45,
    avgPrice: "£440,000",
    rating: 4.7,
    reviewCount: 112,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Combe Residential launched in 2010 with a clear focus: the southern Bath villages that the big city-centre agents often overlook. Based in Combe Down itself, the team knows this part of Bath with a depth that distant offices simply cannot match. Streets, schools, transport links, the local pubs: they live and work where they sell.\n\nTheir coverage takes in Combe Down, Odd Down, Bear Flat, Widcombe, and Oldfield Park. With 45 properties on their books and an average price of £440,000, they offer a curated, quality service rather than a high-volume operation. Sellers benefit from an agent who genuinely understands what makes each southern Bath village distinct.\n\nA 4.7 rating from 112 reviews confirms that the hyper-local approach resonates with clients. For anyone buying or selling in the southern half of Bath, Combe Residential offers a level of neighbourhood expertise that is difficult to find elsewhere.",
    whyChoose: [
      "Rated 4.7/5 from 112 client reviews",
      "Hyper-local specialists in south Bath villages",
      "Based in Combe Down with unmatched neighbourhood knowledge",
      "Independent, quality-focused approach to every instruction",
    ],
    faq: [
      {
        question: "What areas does Combe Residential cover?",
        answer:
          "Combe Residential covers Combe Down, Odd Down, Bear Flat, Widcombe, and Oldfield Park. They specialise in the southern Bath villages and surrounding areas on the city's southern edge.",
      },
      {
        question: "What services does Combe Residential offer?",
        answer:
          "Combe Residential provides residential sales and lettings. Their focus is on personal, local service with honest valuations and considered marketing for properties in south Bath.",
      },
      {
        question: "What are Combe Residential's opening hours?",
        answer:
          "Combe Residential is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "leaders-bath",
    name: "Leaders Letting & Estates",
    branch: "Bath",
    address: "8a Milsom Street",
    postcode: "BA1 1DA",
    phone: "01225 787878",
    website: "https://www.leaders.co.uk/our-offices/somerset/estate-agents-in-bath/",
    email: "bath@leaders.co.uk",
    description:
      "Leaders Bath is part of one of the UK's largest lettings groups, with their prominent Milsom Street office handling both sales and lettings across the city. They offer professional service backed by national infrastructure, covering everything from city-centre apartments to suburban family homes.",
    established: 1983,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Bath City Centre", "Weston", "Newbridge", "Lower Weston", "Oldfield Park", "Twerton"],
    propertyCount: 100,
    avgPrice: "£370,000",
    rating: 4.1,
    reviewCount: 198,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Leaders is one of the UK's largest lettings-focused estate agency groups, and their Bath office on Milsom Street is a prominent presence in the city centre. Since 1983, the brand has built its reputation on managed lettings, and the Bath team carries that expertise forward with a substantial rental portfolio.\n\nThe team covers Bath's western and southern suburbs: Weston, Newbridge, Lower Weston, Oldfield Park, and Twerton. With 100 properties on their books and an average price of £370,000, they serve the practical, everyday end of the market where reliable service and fair pricing matter most.\n\nWith 198 client reviews and a 4.1 rating, Leaders Bath provides a dependable choice backed by serious national infrastructure. Their early opening at 08:30 and property management capabilities make them a strong option for landlords who want professional, no-fuss rental management.",
    whyChoose: [
      "Part of one of the UK's largest lettings groups",
      "Rated 4.1/5 from 198 client reviews",
      "Early opening at 08:30 for maximum accessibility",
      "Strong property management and landlord services",
    ],
    faq: [
      {
        question: "What areas does Leaders Bath cover?",
        answer:
          "Leaders Bath covers Bath City Centre, Weston, Newbridge, Lower Weston, Oldfield Park, and Twerton. They focus on Bath's western suburbs and more affordable residential areas.",
      },
      {
        question: "What services does Leaders offer in Bath?",
        answer:
          "Leaders Bath provides residential sales, lettings, and property management. Their lettings heritage means particularly strong support for landlords, from tenant sourcing to full management.",
      },
      {
        question: "What are Leaders Bath's opening hours?",
        answer:
          "Leaders Bath is open Monday to Friday from 08:30 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "holbrook-jones-bath",
    name: "Holbrook Jones",
    branch: "Bear Flat",
    address: "48 Wells Road, Bear Flat",
    postcode: "BA2 3AG",
    phone: "01225 338866",
    website: "https://www.holbrookjones.co.uk",
    email: "info@holbrookjones.co.uk",
    description:
      "Holbrook Jones is a boutique independent estate agent on Wells Road in the popular Bear Flat neighbourhood. They provide attentive, personal service for residential sales across south Bath, with strong knowledge of Bear Flat, Combe Down, Widcombe, and the surrounding areas. Their smaller operation means dedicated attention for every client.",
    established: 2015,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Bear Flat", "Combe Down", "Widcombe", "Oldfield Park", "Odd Down"],
    propertyCount: 30,
    avgPrice: "£465,000",
    rating: 4.8,
    reviewCount: 78,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Holbrook Jones opened on Wells Road in 2015 and has quickly become one of Bear Flat's most trusted property names. Their boutique approach means a deliberately small portfolio, typically around 30 properties, ensuring every seller receives the time and attention that their home deserves.\n\nThe team covers south Bath's most appealing neighbourhoods: Bear Flat, Combe Down, Widcombe, Oldfield Park, and Odd Down. Living and working in the area gives them a feel for the local market that remote city-centre offices lack. They know which streets catch the morning sun, which roads are quiet, and where the best schools are.\n\nWith a 4.8 rating from 78 reviews, Holbrook Jones has earned an outstanding reputation in a short time. Their smaller scale is a deliberate choice, prioritising quality of service over volume of instructions. For sellers in south Bath, this focused, neighbourhood-first approach is a compelling alternative to the larger chains.",
    whyChoose: [
      "Rated 4.8/5 from 78 client reviews",
      "Boutique agency embedded in the Bear Flat community",
      "Deliberately small portfolio for dedicated client attention",
      "Deep local knowledge of south Bath neighbourhoods",
    ],
    faq: [
      {
        question: "What areas does Holbrook Jones Bear Flat cover?",
        answer:
          "Holbrook Jones covers Bear Flat, Combe Down, Widcombe, Oldfield Park, and Odd Down. They specialise in south Bath's residential neighbourhoods and have detailed, street-level knowledge of the area.",
      },
      {
        question: "What services does Holbrook Jones offer in Bear Flat?",
        answer:
          "Holbrook Jones provides residential sales and lettings. Their boutique approach means every property benefits from personal marketing, realistic pricing, and hands-on support throughout the sale.",
      },
      {
        question: "What are Holbrook Jones Bear Flat's opening hours?",
        answer:
          "Holbrook Jones is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
]
